import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import userAPI from '../services/userAPI';
import {
  Users, Search, Filter, Edit, Trash2, Plus, Eye, Shield,
  UserCheck, UserX, Mail, Phone, Calendar, MapPin, Star,
  Settings, Crown, Briefcase, User, Clock, CheckCircle,
  XCircle, AlertCircle, MoreVertical, Download, Upload,
  Loader, RefreshCw
} from 'lucide-react';
import './UserManagement.css';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // API state management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [userStats, setUserStats] = useState({
    total_users: 0,
    active_users: 0,
    admin_users: 0,
    manager_users: 0,
    staff_users: 0,
    guest_users: 0,
    verified_users: 0
  });

  const normalizeUserStats = (stats = {}) => ({
    total_users: stats.total_users ?? stats.totalUsers ?? stats.total ?? userStats.total_users ?? 0,
    active_users: stats.active_users ?? stats.activeUsers ?? stats.active ?? userStats.active_users ?? 0,
    admin_users: stats.admin_users ?? stats.adminUsers ?? stats.admin ?? userStats.admin_users ?? 0,
    manager_users: stats.manager_users ?? stats.managerUsers ?? stats.manager ?? userStats.manager_users ?? 0,
    staff_users: stats.staff_users ?? stats.staffUsers ?? stats.staff ?? userStats.staff_users ?? 0,
    guest_users: stats.guest_users ?? stats.guestUsers ?? stats.regular_users ?? stats.regularUsers ?? stats.guests ?? userStats.guest_users ?? 0,
    verified_users: stats.verified_users ?? stats.verifiedUsers ?? stats.email_verified ?? stats.emailVerified ?? userStats.verified_users ?? 0
  });

  // ===============================
  // API INTEGRATION FUNCTIONS
  // ===============================

  // Load users from API
  const loadUsers = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        page,
        limit: 20,
        search: searchTerm,
        role: filterRole,
        status: filterStatus
      };
      
      const response = await userAPI.getAllUsers(filters);
      
      if (response.success) {
        const usersData = Array.isArray(response.data)
          ? response.data
          : response.data?.users || [];

        const paginationData = response.pagination || response.data?.pagination || {};

        setUsers(usersData);
        setPagination({
          currentPage: paginationData.currentPage ?? 1,
          totalPages: paginationData.totalPages ?? 1,
          totalUsers: paginationData.totalItems ?? paginationData.totalUsers ?? usersData.length,
          hasNextPage: paginationData.hasNext ?? paginationData.hasNextPage ?? false,
          hasPrevPage: paginationData.hasPrev ?? paginationData.hasPrevPage ?? false
        });
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (err) {
      setError(err.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load user statistics
  const loadUserStats = async () => {
    try {
      const response = await userAPI.getUserStats();
      if (response.success) {
        const statsData = response.data?.stats || response.data || {};
        setUserStats(normalizeUserStats(statsData));
      }
    } catch (err) {
      console.error('Error loading user stats:', err);
    }
  };

  // Create new user
  const handleCreateUser = async (userData) => {
    try {
      setLoading(true);
      const response = await userAPI.createUser(userData);
      
      if (response.success) {
        showNotification('User created successfully', 'success');
        loadUsers(pagination.currentPage);
        loadUserStats();
        closeModal();
      } else {
        showNotification(response.message || 'Failed to create user', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to create user', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update user
  const handleUpdateUser = async (userId, userData) => {
    try {
      setLoading(true);
      const response = await userAPI.updateUser(userId, userData);
      
      if (response.success) {
        showNotification('User updated successfully', 'success');
        loadUsers(pagination.currentPage);
        closeModal();
      } else {
        showNotification(response.message || 'Failed to update user', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to update user', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update user role
  const handleUpdateUserRole = async (userId, role) => {
    try {
      const response = await userAPI.updateUserRole(userId, role);
      
      if (response.success) {
        showNotification('User role updated successfully', 'success');
        loadUsers(pagination.currentPage);
        loadUserStats();
      } else {
        showNotification(response.message || 'Failed to update user role', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to update user role', 'error');
    }
  };

  // Update user status
  const handleUpdateUserStatus = async (userId, status) => {
    try {
      const response = await userAPI.updateUserStatus(userId, status);
      
      if (response.success) {
        showNotification('User status updated successfully', 'success');
        loadUsers(pagination.currentPage);
        loadUserStats();
      } else {
        showNotification(response.message || 'Failed to update user status', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to update user status', 'error');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      const response = await userAPI.deleteUser(userId);
      
      if (response.success) {
        showNotification('User deleted successfully', 'success');
        loadUsers(pagination.currentPage);
        loadUserStats();
      } else {
        showNotification(response.message || 'Failed to delete user', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to delete user', 'error');
    }
  };

  // Bulk delete users
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      showNotification('Please select users to delete', 'warning');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)?`)) {
      return;
    }
    
    try {
      const response = await userAPI.bulkDeleteUsers(selectedUsers);
      
      if (response.success) {
        showNotification(`${selectedUsers.length} user(s) deleted successfully`, 'success');
        setSelectedUsers([]);
        loadUsers(pagination.currentPage);
        loadUserStats();
      } else {
        showNotification(response.message || 'Failed to delete users', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to delete users', 'error');
    }
  };

  // Export users
  const handleExportUsers = async () => {
    try {
      const filters = {
        role: filterRole,
        status: filterStatus
      };
      
      await userAPI.exportUsers(filters);
      showNotification('Users exported successfully', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to export users', 'error');
    }
  };

  // Import users
  const handleImportUsers = async (file) => {
    try {
      setLoading(true);
      const response = await userAPI.importUsers(file);
      
      if (response.success) {
        showNotification(`Successfully imported ${response.data.imported} users`, 'success');
        loadUsers(pagination.currentPage);
        loadUserStats();
        closeModal();
      } else {
        showNotification(response.message || 'Failed to import users', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to import users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show notification helper
  const showNotification = (message, type) => {
    // You can integrate with a toast notification library here
    console.log(`[${type.toUpperCase()}] ${message}`);
    // For now, using alert - replace with proper notification system
    if (type === 'error') {
      alert(`Error: ${message}`);
    }
  };

  // ===============================
  // EFFECT HOOKS
  // ===============================

  // Load data on component mount and when filters change
  useEffect(() => {
    loadUsers(1);
    loadUserStats();
  }, []);

  // Reload users when filters change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        loadUsers(1);
      } else {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        loadUsers(1);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, filterRole, filterStatus]);

  // ===============================
  // MOCK ROLES DATA (to be replaced with API)
  // ===============================

  const [roles] = useState([
    {
      id: 1,
      name: 'admin',
      displayName: 'Administrator',
      description: 'Full system access and management capabilities',
      permissions: ['all'],
      userCount: userStats.admin_users || 0,
      color: '#e74c3c'
    },
    {
      id: 2,
      name: 'manager',
      displayName: 'Manager',
      description: 'Hotel operations and staff management',
      permissions: ['hotel_management', 'user_management', 'reports', 'bookings'],
      userCount: userStats.manager_users || 0,
      color: '#f39c12'
    },
    {
      id: 3,
      name: 'staff',
      displayName: 'Staff',
      description: 'Daily operations and guest services',
      permissions: ['bookings', 'guests', 'rooms'],
      userCount: userStats.staff_users || 0,
      color: '#3498db'
    },
    {
      id: 4,
      name: 'guest',
      displayName: 'Guest',
      description: 'Booking and account management',
      permissions: ['booking', 'profile'],
      userCount: userStats.guest_users || 0,
      color: '#27ae60'
    }
  ]);

  const [permissions] = useState([
    { key: 'all', name: 'Full System Access', category: 'System' },
    { key: 'hotel_management', name: 'Hotel Management', category: 'Operations' },
    { key: 'user_management', name: 'User Management', category: 'Administration' },
    { key: 'reports', name: 'Reports & Analytics', category: 'Analytics' },
    { key: 'bookings', name: 'Booking Management', category: 'Operations' },
    { key: 'guests', name: 'Guest Services', category: 'Service' },
    { key: 'rooms', name: 'Room Management', category: 'Operations' },
    { key: 'booking', name: 'Make Bookings', category: 'Guest' },
    { key: 'profile', name: 'Profile Management', category: 'Personal' }
  ]);

  // ===============================
  // UTILITY FUNCTIONS
  // ===============================

  const getRoleColor = (role) => {
    const roleConfig = roles.find(r => r.name === role);
    return roleConfig ? roleConfig.color : '#95a5a6';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#27ae60';
      case 'inactive': return '#f39c12';
      case 'suspended': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return Clock;
      case 'suspended': return XCircle;
      default: return AlertCircle;
    }
  };

  // Since we're now using server-side filtering via API,
  // we don't need client-side filtering anymore
  const filteredUsers = users;

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setModalType('');
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.user_id));
    }
  };

  const bulkAction = (action) => {
    switch (action) {
      case 'delete':
        handleBulkDelete();
        break;
      case 'activate':
        selectedUsers.forEach(userId => handleUpdateUserStatus(userId, 'active'));
        break;
      case 'deactivate':
        selectedUsers.forEach(userId => handleUpdateUserStatus(userId, 'inactive'));
        break;
      default:
        console.log(`Performing ${action} on users:`, selectedUsers);
    }
    setSelectedUsers([]);
  };

  const tabs = [
    { key: 'users', label: 'Users', icon: Users },
    { key: 'roles', label: 'Roles & Permissions', icon: Shield }
  ];

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-content">
          <div className="header-left">
            <Users size={28} />
            <div>
              <h1>User Management</h1>
              <p>Manage users, roles, and permissions</p>
            </div>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-outline"
              onClick={() => openModal('import')}
            >
              <Upload size={16} />
              Import Users
            </button>
            <button 
              className="btn btn-outline"
              onClick={handleExportUsers}
            >
              <Download size={16} />
              Export
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => openModal('add')}
            >
              <Plus size={16} />
              Add User
            </button>
          </div>
        </div>
      </div>

      <div className="user-management-nav">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.key}
              className={`nav-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <IconComponent size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="user-management-content">
        {activeTab === 'users' && (
          <>
            <div className="content-controls">
              <div className="search-bar">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-controls">
                <Filter size={16} />
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="staff">Staff</option>
                  <option value="guest">Guest</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            {selectedUsers.length > 0 && (
              <div className="bulk-actions">
                <span>{selectedUsers.length} users selected</span>
                <div className="bulk-buttons">
                  <button 
                    className="btn btn-outline"
                    onClick={() => bulkAction('activate')}
                  >
                    <CheckCircle size={14} />
                    Activate
                  </button>
                  <button 
                    className="btn btn-outline"
                    onClick={() => bulkAction('deactivate')}
                  >
                    <Clock size={14} />
                    Deactivate
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => bulkAction('delete')}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            )}

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={selectAllUsers}
                      />
                    </th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Bookings</th>
                    <th>Spent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="loading-cell">
                        <div className="loading-content">
                          <Loader className="loading-spinner" size={24} />
                          <span>Loading users...</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="7" className="error-cell">
                        <div className="error-content">
                          <AlertCircle size={24} color="#e74c3c" />
                          <span>{error}</span>
                          <button 
                            className="btn btn-outline btn-sm"
                            onClick={() => loadUsers(pagination.currentPage)}
                          >
                            <RefreshCw size={16} />
                            Retry
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-cell">
                        <div className="empty-content">
                          <Users size={48} color="#95a5a6" />
                          <h3>No users found</h3>
                          <p>Try adjusting your search filters or add a new user.</p>
                          <button 
                            className="btn btn-primary"
                            onClick={() => openModal('add')}
                          >
                            <Plus size={16} />
                            Add First User
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map(user => {
                      const StatusIcon = getStatusIcon(user.status);
                      return (
                        <tr key={user.user_id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.user_id)}
                              onChange={() => handleUserSelection(user.user_id)}
                            />
                          </td>
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar">
                                {user.avatar ? (
                                  <img src={user.avatar} alt={user.full_name} />
                                ) : (
                                  <User size={20} />
                                )}
                                {user.email_verified && (
                                  <div className="verified-badge">
                                    <CheckCircle size={12} />
                                  </div>
                                )}
                              </div>
                              <div className="user-info">
                                <strong>{user.full_name}</strong>
                                <small>{user.email}</small>
                                <small>{user.phone || 'No phone'}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span 
                              className="role-badge"
                              style={{ backgroundColor: getRoleColor(user.role) }}
                              onClick={() => openModal('editRole', user)}
                              title="Click to edit role"
                            >
                              {user.role === 'admin' && <Crown size={12} />}
                              {user.role === 'manager' && <Briefcase size={12} />}
                              {user.role === 'staff' && <UserCheck size={12} />}
                              {user.role === 'guest' && <User size={12} />}
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(user.status) }}
                          >
                            <StatusIcon size={12} />
                            {user.status}
                          </span>
                        </td>
                        <td>
                          <div className="date-cell">
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                            {user.lastLogin && (
                              <small>{new Date(user.lastLogin).toLocaleTimeString()}</small>
                            )}
                          </div>
                        </td>
                        <td>{user.totalBookings}</td>
                        <td>${user.totalSpent}</td>
                        <td>
                          <div className="user-actions">
                            <button 
                              className="btn-icon"
                              onClick={() => openModal('view', user)}
                            >
                              <Eye size={14} />
                            </button>
                            <button 
                              className="btn-icon"
                              onClick={() => openModal('edit', user)}
                            >
                              <Edit size={14} />
                            </button>
                            <button 
                              className="btn-icon"
                              onClick={() => openModal('more', user)}
                            >
                              <MoreVertical size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  <span>
                    Showing {((pagination.currentPage - 1) * 20) + 1} to{' '}
                    {Math.min(pagination.currentPage * 20, pagination.totalUsers)} of{' '}
                    {pagination.totalUsers} users
                  </span>
                </div>
                <div className="pagination-buttons">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => loadUsers(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrevPage || loading}
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, pagination.currentPage - 2) + i;
                    if (pageNum <= pagination.totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`btn btn-outline btn-sm ${pageNum === pagination.currentPage ? 'active' : ''}`}
                          onClick={() => loadUsers(pageNum)}
                          disabled={loading}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => loadUsers(pagination.currentPage + 1)}
                    disabled={!pagination.hasNextPage || loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'roles' && (
          <div className="roles-section">
            <div className="roles-header">
              <h3>Roles & Permissions</h3>
              <button 
                className="btn btn-primary"
                onClick={() => openModal('add-role')}
              >
                <Plus size={16} />
                Add Role
              </button>
            </div>

            <div className="roles-grid">
              {roles.map(role => (
                <div key={role.id} className="role-card">
                  <div className="role-header">
                    <div className="role-info">
                      <div 
                        className="role-color"
                        style={{ backgroundColor: role.color }}
                      ></div>
                      <div>
                        <h4>{role.displayName}</h4>
                        <p>{role.description}</p>
                      </div>
                    </div>
                    <div className="role-stats">
                      <span className="user-count">{role.userCount} users</span>
                    </div>
                  </div>

                  <div className="role-permissions">
                    <h5>Permissions:</h5>
                    <div className="permissions-list">
                      {role.permissions.map(permission => {
                        const permissionData = permissions.find(p => p.key === permission);
                        return (
                          <span key={permission} className="permission-tag">
                            {permissionData ? permissionData.name : permission}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <div className="role-actions">
                    <button 
                      className="btn btn-outline"
                      onClick={() => openModal('edit-role', role)}
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => openModal('delete-role', role)}
                      disabled={role.userCount > 0}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="permissions-section">
              <h3>Available Permissions</h3>
              <div className="permissions-grid">
                {Object.entries(
                  permissions.reduce((acc, permission) => {
                    if (!acc[permission.category]) {
                      acc[permission.category] = [];
                    }
                    acc[permission.category].push(permission);
                    return acc;
                  }, {})
                ).map(([category, categoryPermissions]) => (
                  <div key={category} className="permission-category">
                    <h4>{category}</h4>
                    <div className="category-permissions">
                      {categoryPermissions.map(permission => (
                        <span key={permission.key} className="permission-item">
                          {permission.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalType === 'add' && 'Add New User'}
                {modalType === 'edit' && `Edit ${selectedUser?.name}`}
                {modalType === 'view' && `User Details - ${selectedUser?.name}`}
                {modalType === 'more' && `User Actions - ${selectedUser?.name}`}
                {modalType === 'add-role' && 'Add New Role'}
                {modalType === 'edit-role' && `Edit Role - ${selectedUser?.displayName}`}
                {modalType === 'delete-role' && 'Delete Role'}
                {modalType === 'import' && 'Import Users'}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {modalType === 'view' && selectedUser && (
                <div className="user-details">
                  <div className="user-profile">
                    <div className="profile-avatar">
                      {selectedUser.avatar ? (
                        <img src={selectedUser.avatar} alt={selectedUser.name} />
                      ) : (
                        <User size={40} />
                      )}
                      {selectedUser.verified && (
                        <div className="verified-badge-large">
                          <CheckCircle size={16} />
                        </div>
                      )}
                    </div>
                    <div className="profile-info">
                      <h3>{selectedUser.name}</h3>
                      <p>{selectedUser.email}</p>
                      <span 
                        className="role-badge"
                        style={{ backgroundColor: getRoleColor(selectedUser.role) }}
                      >
                        {selectedUser.role}
                      </span>
                    </div>
                  </div>

                  <div className="details-grid">
                    <div className="detail-section">
                      <h4>Contact Information</h4>
                      <div className="detail-row">
                        <Mail size={16} />
                        <span>{selectedUser.email}</span>
                      </div>
                      <div className="detail-row">
                        <Phone size={16} />
                        <span>{selectedUser.phone}</span>
                      </div>
                      <div className="detail-row">
                        <MapPin size={16} />
                        <span>{selectedUser.address}</span>
                      </div>
                    </div>

                    <div className="detail-section">
                      <h4>Account Information</h4>
                      <div className="detail-row">
                        <Calendar size={16} />
                        <span>Joined: {new Date(selectedUser.joinDate).toLocaleDateString()}</span>
                      </div>
                      <div className="detail-row">
                        <Clock size={16} />
                        <span>Last Login: {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString() : 'Never'}</span>
                      </div>
                      <div className="detail-row">
                        <Shield size={16} />
                        <span>2FA: {selectedUser.twoFactor ? 'Enabled' : 'Disabled'}</span>
                      </div>
                    </div>

                    {selectedUser.role === 'guest' && (
                      <div className="detail-section">
                        <h4>Booking Statistics</h4>
                        <div className="detail-row">
                          <Calendar size={16} />
                          <span>Total Bookings: {selectedUser.totalBookings}</span>
                        </div>
                        <div className="detail-row">
                          <Star size={16} />
                          <span>Total Spent: ${selectedUser.totalSpent}</span>
                        </div>
                        <div className="detail-row">
                          <Star size={16} />
                          <span>Loyalty Points: {selectedUser.loyaltyPoints}</span>
                        </div>
                      </div>
                    )}

                    {(selectedUser.role === 'staff' || selectedUser.role === 'manager') && (
                      <div className="detail-section">
                        <h4>Work Information</h4>
                        <div className="detail-row">
                          <Briefcase size={16} />
                          <span>Department: {selectedUser.department}</span>
                        </div>
                        <div className="detail-row">
                          <UserCheck size={16} />
                          <span>Position: {selectedUser.position}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {modalType === 'more' && selectedUser && (
                <div className="user-actions-modal">
                  <div className="action-buttons">
                    <button className="action-btn">
                      <Mail size={16} />
                      Send Email
                    </button>
                    <button className="action-btn">
                      <UserCheck size={16} />
                      Reset Password
                    </button>
                    <button className="action-btn">
                      <Shield size={16} />
                      Enable 2FA
                    </button>
                    <button className="action-btn">
                      <Eye size={16} />
                      View Activity Log
                    </button>
                    <button className="action-btn danger">
                      <UserX size={16} />
                      Suspend User
                    </button>
                    <button className="action-btn danger">
                      <Trash2 size={16} />
                      Delete User
                    </button>
                  </div>
                </div>
              )}

              {modalType === 'import' && (
                <div className="import-section">
                  <div className="import-instructions">
                    <h4>Import Users from CSV</h4>
                    <p>Upload a CSV file with user information. The file should include columns for:</p>
                    <ul>
                      <li>Name</li>
                      <li>Email</li>
                      <li>Phone</li>
                      <li>Role</li>
                      <li>Department (optional)</li>
                      <li>Position (optional)</li>
                    </ul>
                  </div>
                  <div className="file-upload">
                    <input type="file" accept=".csv" id="csvFile" />
                    <label htmlFor="csvFile" className="upload-label">
                      <Upload size={20} />
                      Choose CSV File
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeModal}>
                Close
              </button>
              {modalType === 'import' && (
                <button className="btn btn-primary">
                  Import Users
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;